import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Badge, Gap, Icon, Spacing, Text } from '@reservamos/elements';
import './PassengersHeading.scss';
import adultIcon from 'images/passengers/adult-icon.png';
import infantIcon from 'images/passengers/infant-icon.png';
import childIcon from 'images/passengers/child-icon.png';
import petBadge from 'images/badges/pet.svg';
import NewSeat from 'ui/atoms/NewSeat';
import OverbookingBadge from 'ui/atoms/OverbookingBadge';
import { isPetFriendlySeat } from 'utils/purchase/seats';
import { useTranslation } from 'react-i18next';
import { getSeatZoneNumberForPassengerSeat } from 'utils/seatPricing';

/**
 * Render the seat number. It could be one or two depending on round trip
 * @param {Object} props
 * @param {Array} props.seats Seats
 * @param {boolean} [props.applySeatZones] - When per-leg zone lists are empty, gates zone styling for both legs.
 * @param {number} [props.departureSeatCountForZone] - How many leading seats belong to departure (connections / ida-vuelta).
 * @param {Array} [props.seatPriceZonesDeparture] - seat_price_zones for departure trip; non-empty implies zones apply on that leg.
 * @param {Array} [props.seatPriceZonesReturn] - seat_price_zones for return trip; non-empty implies zones apply on that leg.
 * @returns JSX Element
 */
const SeatNumberWrapper = ({
  seats,
  applySeatZones,
  departureSeatCountForZone,
  seatPriceZonesDeparture,
  seatPriceZonesReturn,
}) => {
  const hasDepartureZoneData =
    Array.isArray(seatPriceZonesDeparture) && seatPriceZonesDeparture.length > 0;
  const hasReturnZoneData = Array.isArray(seatPriceZonesReturn) && seatPriceZonesReturn.length > 0;
  const departureZonesOn = hasDepartureZoneData || applySeatZones;
  const returnZonesOn = hasReturnZoneData || applySeatZones;
  const { t } = useTranslation();

  // Sort seats so oversold seats appear at the end
  const sortedSeats = useMemo(() => {
    if (!seats || !seats.length) return [];

    return seats
      .filter((seat) => !!seat && !seat.isPickedAsAdjacent)
      .sort((a, b) => {
        if (a.category === 'oversold' && b.category !== 'oversold') return 1;
        if (a.category !== 'oversold' && b.category === 'oversold') return -1;
        return 0;
      });
  }, [seats]);

  if (!sortedSeats.length) return null;

  return sortedSeats.map((seat, seatIndex) => {
    const isReturnSegment =
      typeof departureSeatCountForZone === 'number' && seatIndex >= departureSeatCountForZone;
    const legAppliesZones = isReturnSegment ? returnZonesOn : departureZonesOn;
    const zonesForSeat = isReturnSegment ? seatPriceZonesReturn : seatPriceZonesDeparture;
    const zone =
      legAppliesZones && Array.isArray(zonesForSeat) && zonesForSeat.length > 0
        ? getSeatZoneNumberForPassengerSeat(seat, zonesForSeat)
        : undefined;

    return (
      <React.Fragment
        key={[
          seat.tripSlug,
          seat.number,
          seat.seatFloor,
          seat.seatLevel,
          seat.seatFareReference ?? seat.seat_fare_reference,
        ]
          .filter((part) => part != null && part !== '')
          .join('-')}
      >
        <Spacing size="XS" alignItems="center">
          {seat.number && (
            <NewSeat
              number={seat.number}
              type="selected"
              category={seat.category}
              showIcon={!isPetFriendlySeat(seat.category)}
              isSmall
              useNewDesign
              zone={zone}
              customRgbColor={seat.rgbColor ?? seat.rgb_color}
            />
          )}
          {seat.seatFloor && (
            <Badge roundCorners type="gray">
              <Text elementType="span" size="S" weight="semibold">
                {t('deck')}
                <Text elementType="span" size="S" weight="bold">
                  {' '}
                  {seat.seatFloor}
                </Text>
              </Text>
            </Badge>
          )}
          {seat.category === 'oversold' && <OverbookingBadge hasIcon />}
        </Spacing>
        {seat.isAdjacentPicked && (
          <NewSeat
            number={seat.adjacentSeats.numbers[0]}
            type="selected"
            category="adjacent"
            showIcon
            isSmall
            useNewDesign
          />
        )}
      </React.Fragment>
    );
  });
};

/**
 * This component is used to render the heading of the passengers card
 * @param {Object} Props
 * @param {string} Props.title Title of the card
 * @param {string} Props.category Category of the passenger
 * @param {Array} Props.seats Seats
 * @param {node} Props.rightContent Content to be rendered on the right
 * @param {boolean} [Props.applySeatZones] When per-leg zone arrays are empty, enables zone styling for both legs
 * @param {number} [Props.departureSeatCountForZone] Count of departure seats in `seats` (for round trip / connections)
 * @param {Array<object>} [Props.seatPriceZonesDeparture] seat_price_zones for departure
 * @param {Array<object>} [Props.seatPriceZonesReturn] seat_price_zones for return
 * @returns JSX Element
 */
const PassengersHeading = ({
  title,
  rightContent,
  category,
  seats,
  applySeatZones,
  departureSeatCountForZone,
  seatPriceZonesDeparture,
  seatPriceZonesReturn,
}) => {
  const icons = {
    default: adultIcon,
    general: adultIcon,
    infant: infantIcon,
    minor: childIcon,
    pet_friendly: petBadge,
  };

  return (
    <div className="passengers-heading">
      <Gap alignItems="center">
        {category && <Icon type={icons[category] || icons.default} />}
        <Text weight="bold">{title}</Text>
        {seats && (
          <SeatNumberWrapper
            seats={seats}
            applySeatZones={applySeatZones}
            departureSeatCountForZone={departureSeatCountForZone}
            seatPriceZonesDeparture={seatPriceZonesDeparture}
            seatPriceZonesReturn={seatPriceZonesReturn}
          />
        )}
      </Gap>
      {rightContent}
    </div>
  );
};

SeatNumberWrapper.propTypes = {
  seats: PropTypes.arrayOf(PropTypes.object).isRequired,
  applySeatZones: PropTypes.bool,
  departureSeatCountForZone: PropTypes.number,
  seatPriceZonesDeparture: PropTypes.arrayOf(PropTypes.object),
  seatPriceZonesReturn: PropTypes.arrayOf(PropTypes.object),
};

SeatNumberWrapper.defaultProps = {
  applySeatZones: false,
  departureSeatCountForZone: undefined,
  seatPriceZonesDeparture: [],
  seatPriceZonesReturn: [],
};

PassengersHeading.propTypes = {
  title: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  seats: PropTypes.arrayOf(PropTypes.object),
  rightContent: PropTypes.node,
  applySeatZones: PropTypes.bool,
  departureSeatCountForZone: PropTypes.number,
  seatPriceZonesDeparture: PropTypes.arrayOf(PropTypes.object),
  seatPriceZonesReturn: PropTypes.arrayOf(PropTypes.object),
};

PassengersHeading.defaultProps = {
  seats: [],
  rightContent: null,
  applySeatZones: false,
  departureSeatCountForZone: undefined,
  seatPriceZonesDeparture: [],
  seatPriceZonesReturn: [],
};

export default PassengersHeading;
